import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimateHeight, { Height } from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { AmlHistoryRequest, AmlStatusEnum } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  investigationHistorySelectors,
  userSelectors,
} from '@/redux/selectors';
import { investigationHistoryActions } from '@/redux/actions';
import { InvestigationHistoryItemData } from '@/redux/models';
import {
  Button,
  HistoryEmpty,
  InvestigationHistoryTableItem,
  Loader,
  SectionBox,
} from '@/components';
import { navigationPages } from '@/config';
import { castDateFilters, getFormattedDate } from '@/helpers';
import { TableTools } from './components';
import { InputSearch } from '@/components/_inputs';
import useDevice from '@/hooks/useDevice';
import styles from './InvestigationHistory.module.sass';

const STORE_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Note: `createTo` should add 1 day because of backend filtering logic
 */
const getFilledFilters = (filterList: Record<string, any>) => {
  const filters = {
    ...(!!filterList.asset ? { asset: filterList.asset } : {}),
    ...(!!filterList.status ? { status: filterList.status } : {}),
    ...(!!filterList.createFrom
      ? { createFrom: getFormattedDate(filterList.createFrom, 'YYYY-MM-DD') }
      : {}),
    ...(!!filterList.createTo
      ? {
          createTo: getFormattedDate(
            filterList.createTo.add(1, 'days'),
            'YYYY-MM-DD',
          ),
        }
      : {}),
  };

  return Object.keys(filters).length ? { filter: filters } : {};
};

interface InvestigationHistoryProps {}

const InvestigationHistory: FC<InvestigationHistoryProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const [loaderHeight, setLoaderHeight] = useState<Height>(0);
  const { loading, error, data, filters, queryHash } = useAppSelector(
    investigationHistorySelectors.getInvestigationHistorySlice,
  );
  const searchFilters = useMemo(() => castDateFilters(filters), [filters]);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);
  const pageTitle = t('translation.requests_history_title');
  const isShowMoreAvailable = !!data && data.totalCount - data.items.length > 0;
  const isEmpty = !data || data.items.length === 0;

  useEffect(() => {
    // fetch data on the first entrance, honouring filters restored from the store
    if (!data) {
      dispatch(
        investigationHistoryActions.fetchInvestigationHistory({
          queryHash,
          ...getFilledFilters(searchFilters),
        }),
      );
    }
  }, [data]);

  useEffect(() => {
    setLoaderHeight(loading ? 'auto' : 0);
  }, [loading]);

  const showMoreHandler = useCallback(() => {
    if (data) {
      if (data.totalCount - data.items.length > 0) {
        const currentPage = Math.ceil(data.items.length / data.pageLimit);

        const payload: AmlHistoryRequest = {
          // backend counts pages from 0
          page: currentPage,
          queryHash,
          ...getFilledFilters(searchFilters),
        };

        dispatch(
          investigationHistoryActions.fetchInvestigationHistory(payload),
        );
      }
    }
  }, [dispatch, data, queryHash, searchFilters]);

  const detailsHandler = useCallback(
    (item: InvestigationHistoryItemData) => {
      if (item.status !== AmlStatusEnum.PENDING && item.connections) {
        navigate(navigationPages.INVESTIGATION_DETAILS.path, {
          state: { extendedItem: item },
        });
      }
    },
    [navigate],
  );

  const searchHandler = useCallback(
    (filtersOverride: Record<string, any> | null = null, term?: string) => {
      const payload: AmlHistoryRequest = {
        queryHash: term ?? queryHash,
        ...getFilledFilters(filtersOverride || searchFilters),
      };

      dispatch(investigationHistoryActions.fetchInvestigationHistory(payload));
    },
    [dispatch, queryHash, searchFilters],
  );

  const queryChangeHandler = useCallback(
    (newValue: string) => {
      dispatch(investigationHistoryActions.setQueryHash(newValue));

      if (newValue.length === 0) {
        searchHandler(searchFilters, '');
      }
    },
    [dispatch, searchHandler, searchFilters],
  );

  const filtersChanged = (key: string, value: any) => {
    dispatch(
      investigationHistoryActions.setFilters({
        ...filters,
        [key]: dayjs.isDayjs(value) ? value.format(STORE_DATE_FORMAT) : value,
      }),
    );
  };

  const onFilterApply = () => {
    searchHandler();
  };

  const onFilterClear = () => {
    dispatch(investigationHistoryActions.resetFilters());
    searchHandler({});
  };

  return (
    <SectionBox>
      {{
        title: pageTitle,
        actions: isEmailConfirmed && (
          <TableTools
            loading={loading}
            onSearchChangeHandler={(newVal) => queryChangeHandler(newVal)}
            onSearchHandler={searchHandler}
            term={queryHash}
            filters={searchFilters}
            onFiltersChangeHandler={filtersChanged}
            onApplyHandler={onFilterApply}
            onClearHandler={onFilterClear}
            hideSearch={isMobile}
          />
        ),
        body: (
          <>
            {isMobile && isEmailConfirmed && (
              <InputSearch
                id="queryHashMobile"
                name="queryHashMobile"
                value={queryHash}
                onChange={(newVal) => queryChangeHandler(newVal)}
                onSearch={searchHandler}
                readOnly={loading}
                placeholder="address"
                expandable
                className={styles.mobileSearch}
              />
            )}
            <AnimateHeight
              className={styles.loader}
              duration={100}
              height={loaderHeight}
            >
              <Loader />
            </AnimateHeight>

            {isEmpty && (
              <HistoryEmpty
                title={t('translation.nomore_empty_string')}
                desc={t('translation.amlhistory_empty_string')}
              />
            )}

            {!isEmpty && (
              <>
                <div
                  className={`${styles.table} ${
                    loading ? styles.table_blur : ''
                  }`}
                >
                  <div className={styles.table__body}>
                    {data.items.map((item) => (
                      <InvestigationHistoryTableItem
                        key={item.uuid}
                        item={item}
                        loading={loading}
                        onDetails={detailsHandler}
                      />
                    ))}
                  </div>
                </div>

                {isShowMoreAvailable && (
                  <div className={styles.show}>
                    <Button
                      size="m"
                      onClick={showMoreHandler}
                      disabled={loading}
                      nowrap
                    >
                      {t('translation.next_page_btn')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        ),
      }}
    </SectionBox>
  );
};

export default InvestigationHistory;
